import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format } from 'date-fns';
import ChartDateLimiter from '@/components/charts/ChartDateLimiter';
import { parseDateLiteral, inventoryChangeAxisLabel, isPastThreeYearMark, isWithinThreeYearRange } from '@/utils/chartAxisUtils';
import { getChartFontSize } from '@/utils/chartTypography';
import { keepFirstEntryPerMonth } from '@/utils/commonUtils';
import { useRsColors } from '@/hooks/useRsColors';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import { useTheme } from '@/contexts/ThemeContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface PolymerTotalsViewProps {
  data: any[];
  polymer: 'PP' | 'PS' | 'PVC';
  fullBleed?: boolean;
}

type ChartType = 'days-inventory' | 'inventory-change' | 'operating-rate' | 'demand-rate' | 'production' | 'demand' | 'total-sales' | 'exports-total';

interface ChartConfig {
  key: ChartType;
  label: string;
  dataKey: (polymer: string, d: any) => number | null;
  isBillions: boolean;
  isSignedChange: boolean;
  divisor?: number;
}

const getChartConfigs = (polymer: 'PP' | 'PS' | 'PVC'): ChartConfig[] => {
  const psConfigs: ChartConfig[] = [
    {
      key: 'days-inventory',
      label: 'Days of Inventory',
      dataKey: (_, d) => (d.days_of_inventory !== null && d.days_of_inventory !== undefined ? Number(d.days_of_inventory) : null),
      isBillions: false,
      isSignedChange: false,
    },
    {
      key: 'demand-rate',
      label: 'Demand Rate %',
      dataKey: (_, d) => (d.demand_rates !== null && d.demand_rates !== undefined ? Number(d.demand_rates) : null),
      isBillions: false,
      isSignedChange: false,
    },
    {
      key: 'demand',
      label: 'Domestic Demand',
      dataKey: (_, d) => (d.domestic_demand !== null && d.domestic_demand !== undefined ? Number(d.domestic_demand) : null),
      isBillions: true,
      isSignedChange: false,
    },
    {
      key: 'exports-total',
      label: 'Exports Total',
      dataKey: (_, d) => (d.exports !== null && d.exports !== undefined ? Number(d.exports) * 1000 : null),
      isBillions: false,
      isSignedChange: false,
    },
    {
      key: 'inventory-change',
      label: 'Inventory Change (Monthly)',
      dataKey: (_, d) => (d.inventory_change_monthly !== null && d.inventory_change_monthly !== undefined ? Number(d.inventory_change_monthly) : null),
      isBillions: true,
      isSignedChange: true,
    },
    {
      key: 'operating-rate',
      label: 'Operating Rate %',
      dataKey: (_, d) => (d.operating_rates !== null && d.operating_rates !== undefined ? Number(d.operating_rates) : null),
      isBillions: false,
      isSignedChange: false,
    },
    {
      key: 'production',
      label: 'Production',
      dataKey: (_, d) => (d.domestic_production !== null && d.domestic_production !== undefined ? Number(d.domestic_production) : null),
      isBillions: true,
      isSignedChange: false,
    },
    {
      key: 'total-sales',
      label: 'Total Sales',
      dataKey: (_, d) => (d.total_sales !== null && d.total_sales !== undefined ? Number(d.total_sales) : null),
      isBillions: true,
      isSignedChange: false,
    },
  ];

  const ppPvcConfigs: ChartConfig[] = [
    {
      key: 'days-inventory',
      label: 'Days of Inventory',
      dataKey: (_, d) => (d.days_of_inventory !== null && d.days_of_inventory !== undefined ? Number(d.days_of_inventory) : null),
      isBillions: false,
      isSignedChange: false,
    },
    {
      key: 'demand-rate',
      label: 'Demand Rate %',
      dataKey: (_, d) => (d.demand_rates !== null && d.demand_rates !== undefined ? Number(d.demand_rates) : null),
      isBillions: false,
      isSignedChange: false,
    },
    {
      key: 'demand',
      label: 'Domestic Demand',
      dataKey: (_, d) => (d.domestic_demand !== null && d.domestic_demand !== undefined ? Number(d.domestic_demand) : null),
      isBillions: true,
      isSignedChange: false,
    },
    {
      key: 'production',
      label: 'Domestic Production',
      dataKey: (_, d) => (d.domestic_production !== null && d.domestic_production !== undefined ? Number(d.domestic_production) : null),
      isBillions: true,
      isSignedChange: false,
    },
    {
      key: 'exports-total',
      label: 'Exports Total',
      dataKey: (_, d) => (d.exports !== null && d.exports !== undefined ? Number(d.exports) : null),
      isBillions: true,
      isSignedChange: false,
    },
    {
      key: 'inventory-change',
      label: 'Inventory Change (Monthly)',
      dataKey: (_, d) => (d.inventory_change_monthly !== null && d.inventory_change_monthly !== undefined ? Number(d.inventory_change_monthly) : null),
      isBillions: true,
      isSignedChange: true,
    },
    {
      key: 'operating-rate',
      label: 'Operating Rate %',
      dataKey: (_, d) => (d.operating_rates !== null && d.operating_rates !== undefined ? Number(d.operating_rates) : null),
      isBillions: false,
      isSignedChange: false,
    },
    {
      key: 'total-sales',
      label: 'Total Sales',
      dataKey: (_, d) => (d.total_sales !== null && d.total_sales !== undefined ? Number(d.total_sales) : null),
      isBillions: true,
      isSignedChange: false,
    },
  ];

  return polymer === 'PS' ? psConfigs : ppPvcConfigs;
};

const PolymerTotalsView: React.FC<PolymerTotalsViewProps> = ({ data, polymer, fullBleed = false }) => {
  const { isDark } = useTheme();
  const [selectedChart, setSelectedChart] = useState<ChartType>('days-inventory');
  const [scrollIndex, setScrollIndex] = useState(0);

  // Polymer-specific chevron breakpoints
  const getMidBreakpoint = (): number => {
    switch (polymer) {
      case 'PP':
        return 1390;
      case 'PS':
        return 1320;
      case 'PVC':
        return 1390;
      default:
        return 1280;
    }
  };

  const midBreakpoint = getMidBreakpoint();

  // ≤1100px: collapse to 3 buttons at a time. Polymer-specific: show chevrons.
  const [isSmall, setIsSmall] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches,
  );
  const [isMid, setIsMid] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${midBreakpoint}px)`).matches,
  );
  // ≤650px: show only 1 button at a time.
  const [isTiny, setIsTiny] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 650px)').matches,
  );
  // Below 1024px, hide all chart data labels.
  const [hideDataLabels, setHideDataLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );
  // Below 750px, center chart title.
  const [isSmallTitle, setIsSmallTitle] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const rowRef = useRef<HTMLDivElement>(null);
  const rs = useRsColors();
  const visibleCount = isTiny ? 1 : 3;

  useEffect(() => {
    const smallMq = window.matchMedia('(max-width: 1100px)');
    const midMq = window.matchMedia(`(max-width: ${midBreakpoint}px)`);
    const tinyMq = window.matchMedia('(max-width: 650px)');
    const labelsMq = window.matchMedia('(max-width: 1023px)');
    const titleMq = window.matchMedia('(max-width: 749px)');
    const smallHandler = (e: MediaQueryListEvent) => {
      setIsSmall(e.matches);
      setScrollIndex(0);
    };
    const midHandler = (e: MediaQueryListEvent) => setIsMid(e.matches);
    const tinyHandler = (e: MediaQueryListEvent) => {
      setIsTiny(e.matches);
      setScrollIndex(0);
    };
    const labelsHandler = (e: MediaQueryListEvent) => setHideDataLabels(e.matches);
    const titleHandler = (e: MediaQueryListEvent) => setIsSmallTitle(e.matches);
    smallMq.addEventListener('change', smallHandler);
    midMq.addEventListener('change', midHandler);
    tinyMq.addEventListener('change', tinyHandler);
    labelsMq.addEventListener('change', labelsHandler);
    titleMq.addEventListener('change', titleHandler);
    return () => {
      smallMq.removeEventListener('change', smallHandler);
      midMq.removeEventListener('change', midHandler);
      tinyMq.removeEventListener('change', tinyHandler);
      labelsMq.removeEventListener('change', labelsHandler);
      titleMq.removeEventListener('change', titleHandler);
    };
  }, [midBreakpoint]);

  const allCharts = getChartConfigs(polymer);
  const totalCharts = allCharts.length;
  const selectedIndex = allCharts.findIndex((c) => c.key === selectedChart);

  // Move selection by one chart, update the chart below, and keep the button visible.
  const goToChart = (newIndex: number) => {
    const clamped = Math.max(0, Math.min(totalCharts - 1, newIndex));
    setSelectedChart(allCharts[clamped].key);
    if (isSmall) {
      // Keep the selected button inside the visible window
      setScrollIndex((i) => {
        if (clamped < i) return clamped;
        if (clamped > i + visibleCount - 1) return clamped - visibleCount + 1;
        return i;
      });
    } else {
      // Mid band: scroll the selected button into view
      requestAnimationFrame(() => {
        const btn = rowRef.current?.children[clamped] as HTMLElement | undefined;
        btn?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      });
    }
  };

  const handlePrev = () => goToChart(selectedIndex - 1);
  const handleNext = () => goToChart(selectedIndex + 1);

  const chartDefaults = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: { color: rs.offWhite, font: { size: getChartFontSize() } },
        grid: { color: rs.gridLine },
      },
      y: {
        ticks: { color: rs.offWhite, font: { size: getChartFontSize() } },
        grid: { color: rs.gridLine },
      },
    },
  }), [rs]);

  const removeDuplicateMonthlyData = keepFirstEntryPerMonth(data).reverse();

  if (removeDuplicateMonthlyData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-rs-off-white/50">
        Loading data…
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const charts = getChartConfigs(polymer);
  const labels = removeDuplicateMonthlyData.map((d) => formatDate(d.date)).reverse();

  const currentConfig = charts.find((c) => c.key === selectedChart) || charts[0];
  const rawData = removeDuplicateMonthlyData.map((d) => currentConfig.dataKey(polymer, d)).reverse();

  const transformedData = rawData.map((v) => {
    if (v === null || v === undefined) return null;
    if (currentConfig.isBillions) {
      const divisor = currentConfig.divisor || 1_000_000;
      return v / divisor;
    }
    return v;
  });

  const barColors = transformedData.map((v) => {
    if (currentConfig.isSignedChange && v !== null && v < 0) return 'rgba(220,38,38,0.9)';
    return 'rgba(132,204,22,0.85)';
  });

  const formatMillions = (billionValue: number) => {
    const millions = billionValue * 1000;
    const abs = Math.abs(millions);
    if (abs >= 1000) return `${(millions / 1000).toFixed(1)}B`;
    return `${Math.round(millions)}M`;
  };

  const formatBillions = (value: number) => {
    const abs = Math.abs(value);
    const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return `${Number(value.toFixed(digits))}B`;
  };

  const shouldShowEvery3Months = (label: string, index: number) => {
    try {
      const date = new Date(label);
      if (isNaN(date.getTime())) {
        return index % 3 === 0;
      }
      return date.getMonth() % 3 === 0;
    } catch {
      return index % 3 === 0;
    }
  };

  const chartOptions: any = {
    ...chartDefaults,
    plugins: {
      ...(chartDefaults as any).plugins,
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const raw = Number(ctx.raw || 0);
            if (currentConfig.key === 'inventory-change') {
              const inMillions = raw / 1000;
              const abs = Math.abs(inMillions);
              const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
              return `${raw >= 0 ? '+' : ''}${Number(inMillions.toFixed(digits))}M`;
            }
            if (currentConfig.isBillions) {
              const inMillions = raw * 1000;
              const abs = Math.abs(inMillions);
              const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
              return `${raw >= 0 ? '+' : ''}${Number(inMillions.toFixed(digits))}M`;
            }
            return `${raw}`;
          },
        },
      },
      datalabels: {
        // Hidden below 1024px; otherwise show when the visible window spans ≤ 3 years
        display: (context: any) => !hideDataLabels && isWithinThreeYearRange(context?.chart?.data?.labels ?? []),
        color: rs.offWhite,
        font: { size: 11, weight: 'bold' },
        anchor: 'center',
        align: 'center',
        formatter: (value: number | null) => {
          if (value === null || value === undefined) return '';
          if (currentConfig.key === 'demand-rate' || currentConfig.key === 'operating-rate') {
            const numValue = Number(value);
            return numValue.toFixed(2);
          }
          if (currentConfig.key === 'inventory-change') {
            const inMillions = currentConfig.isBillions ? value * 1000 : value / 1000;
            const abs = Math.abs(inMillions);
            const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
            const result = `${Number(inMillions.toFixed(digits))}M`;
            return result;
          }
          if (currentConfig.isBillions) {
            const inMillions = value * 1000;
            const abs = Math.abs(inMillions);
            const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
            return `${Number(inMillions.toFixed(digits))}M`;
          }
          return `${Number(value.toFixed(0))}`;
        },
      },
    },
    scales: {
      ...(chartDefaults as any).scales,
      x: {
        ...(chartDefaults as any).scales.x,
        ticks: {
          ...(chartDefaults as any).scales.x.ticks,
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: function (value: any, index: number) {
            const lbl = this.getLabelForValue(value);
            const d = parseDateLiteral(lbl);
            // <3yr window: every 6 months (MMM yyyy). Once dragged past the
            // 3-year mark, the whole axis switches to year-only. Applies to every metric.
            if (!isNaN(d.getTime())) {
              const yearOnly = isPastThreeYearMark(this.chart.data.labels as any[]);
              return inventoryChangeAxisLabel(d, yearOnly);
            }
            return shouldShowEvery3Months(lbl, index) ? lbl : '';
          },
        },
      },
      y: {
        ...(chartDefaults as any).scales.y,
        ticks: {
          ...(chartDefaults as any).scales.y.ticks,
          callback: (value: any) => {
            const n = Number(value);
            if (currentConfig.isBillions && ['demand', 'production', 'exports-total', 'total-sales', 'inventory-change'].includes(currentConfig.key)) {
              return formatMillions(n);
            }
            return currentConfig.isBillions ? formatBillions(n) : n;
          },
        },
        ...(currentConfig.isBillions ? { title: { display: true, text: currentConfig.key === 'inventory-change' ? 'Monthly lbs' : 'Daily lbs', color: rs.offWhite, font: { size: 12 } } } : {}),
      },
    },
  };

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);

  return (
    <div className={`flex flex-col gap-6 ${fullBleed && isSmall ? '-mx-6 p-0' : 'p-4'}`}>
      {/* ≤1100px: 3 buttons at a time (chevrons window through them).
          1100–1200px: all buttons in a scroll row, chevrons scroll to the next.
          >1200px: full row, no chevrons. */}
      <div className={`flex items-center gap-1 w-full ${isMid ? 'justify-center' : ''}`}>
        {isMid && (
          <button
            onClick={handlePrev}
            disabled={selectedIndex <= 0}
            className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
            aria-label="Previous metric"
          >
            <ChevronLeft size={28} strokeWidth={2.5} />
          </button>
        )}

        <div
          ref={rowRef}
          className={`flex gap-2 ${isSmall && isMid ? 'justify-center' : isSmall ? 'flex-1 justify-center' : 'overflow-x-auto scrollbar-hide flex-1'}`}
        >
          {(isSmall ? charts.slice(scrollIndex, scrollIndex + visibleCount) : charts).map((config) => (
            <button
              key={config.key}
              onClick={() => setSelectedChart(config.key)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors border whitespace-nowrap flex-shrink-0 ${
                selectedChart === config.key
                  ? 'bg-rs-green text-black border-rs-green'
                  : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>

        {isMid && (
          <button
            onClick={handleNext}
            disabled={selectedIndex >= totalCharts - 1}
            className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
            aria-label="Next metric"
          >
            <ChevronRight size={28} strokeWidth={2.5} />
          </button>
        )}
      </div>

      <div className="bg-rs-off-black rounded-lg p-4">
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallTitle ? 'text-center' : ''}`}>{currentConfig.label}</h3>
        <ChartDateLimiter labels={labels} datasets={[{ data: rawData, backgroundColor: barColors }]}>
          {(limitedLabels, limitedDatasets) => {
            const chartData = {
              labels: limitedLabels,
              datasets: [
                {
                  label: currentConfig.label,
                  data: (limitedDatasets[0]?.data ?? []).map((v: any) => {
                    if (v === null || v === undefined) return null;
                    if (currentConfig.isBillions) {
                      const divisor = currentConfig.divisor || 1_000_000;
                      return Number(v) / divisor;
                    }
                    return Number(v);
                  }),
                  backgroundColor: limitedDatasets[0]?.backgroundColor ?? 'rgba(132,204,22,0.85)',
                  borderRadius: 2,
                },
              ],
            };
            return (
              <div className="h-[500px] min-w-0 overflow-hidden">
                <Bar data={chartData} options={chartOptions} plugins={[hoverLinePlugin]} />
              </div>
            );
          }}
        </ChartDateLimiter>
      </div>

      <p className={`text-rs-highlight-gray text-xs ${fullBleed && isSmall ? 'px-4' : ''}`}>Source: American Chemistry Council® (ACC)</p>
    </div>
  );
};

export default PolymerTotalsView;
